import React from 'react';
import styled from 'styled-components';
import {COLORS, SIZES} from '../styleVars';
import {Typography} from '../typography/Typography';

interface SelectButtonProps {
  selected: boolean;
  disabled: boolean;
}

const SelectButton = styled.div<SelectButtonProps>`
  min-width: ${SIZES['72']};
  height: ${SIZES['24']};
  line-height: ${SIZES['16']};
  padding: 0.2rem ${SIZES['16']};
  background-color: ${(props) =>
    props.disabled ? COLORS.lightestGrey : props.selected ? COLORS.fupaSecondary : COLORS.white};
  border: 1px solid ${(props) => (props.selected ? COLORS.fupaSecondary : COLORS.lightGrey)};
  border-radius: ${SIZES['2']};
  outline: none;
  text-decoration: none;
  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  text-align: center;
  box-sizing: border-box;
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  text-transform: none;
`;

interface StyledTypographyProps {
  selected: boolean;
  disabled: boolean;
}

const StyledTypography = styled(Typography)<StyledTypographyProps>`
  &&& {
    color: ${(props) => (props.disabled ? COLORS.grey : props.selected ? COLORS.white : COLORS.darkerGrey)};
  }
`;

interface ToggleButtonProps {
  /**
   * The label of the ToggleButton is passed as child
   */
  children: React.ReactNode;
  /**
   * Defines as which HTML-Element the ToggleButton should be rendered
   */
  component: string | React.ComponentClass<any, any> | React.FunctionComponent<any>;
  /**
   * Disables functionality of ToggleButton
   */
  disabled?: boolean;
  /**
   * Callback when the ToggleButton is clicked.
   */
  onClick: (value: string) => void;
  /**
   * Defines if Button is selected.
   */
  selected?: boolean;
  /**
   * tab-index of the Button, used to customize focus-order
   */
  tabIndex: number;
  /**
   * Value of the ToggleButton, used of identification
   */
  value: string;
  /**
   * @ignore
   */
  className?: string;
}

const ToggleButton: React.FC<ToggleButtonProps> = ({
  disabled = false,
  selected = false,
  children,
  className,
  component,
  onClick,
  tabIndex = 0,
  value,
  ...props
}) => {
  const handleClick = () => {
    if (!disabled) {
      onClick(value);
    }
  };

  return (
    <SelectButton
      tabIndex={tabIndex}
      as={component}
      className={className}
      selected={selected}
      disabled={disabled}
      onClick={handleClick}
      value={value}
      {...props}>
      <StyledTypography disabled={disabled} selected={selected} variant='caption3'>
        {children}
      </StyledTypography>
    </SelectButton>
  );
};

interface ToggleButtonsProps {
  /**
   * Defines the active Child by its value.
   */
  activeValue: string;
  /**
   * Two or more ToggleButton-Elements.
   */
  children: React.ReactNode[];
  /**
   * Callback when activeChild is changed.
   */
  onChange: (buttonValue: string) => void;
}

const ToggleButtons: React.FC<ToggleButtonsProps> = ({children, onChange, activeValue}) => {
  children = children.map((child) => {
    /** @ts-ignore */
    const {value: buttonValue} = child.props;
    const selected = buttonValue === activeValue;

    /** @ts-ignore */
    return React.cloneElement(child, {
      key: buttonValue,
      selected,
      onClick: (buttonValue: string) => onChange(buttonValue),
    });
  });

  return <Container>{children}</Container>;
};

export {ToggleButtons, ToggleButton};
